import { api } from './client';
import { ANALYTICS_DEFAULTS, PAGINATION } from '@/lib/constants';
import type { Granularity } from '@/types/enums';
import type {
  AccountDashboardResponse,
  ConversionsResponse,
  AnalyticsInsightsResponse,
  EfficiencyParams,
  EfficiencyResponse,
  EvolutionParams,
  IdentityPerformance,
  LinkReadinessParams,
  LinkReadinessResponse,
  OverviewResponse,
  OverviewSeriesResponse,
  Page,
  PageParams,
  ReelResponse,
  RetirementCandidateResponse,
  RetirementCandidatesParams,
  TemplateDetail,
  TemplateSummary,
  TemplatesListParams,
  TimeSeries,
  TopAccount,
  TopAccountsParams,
  ViralReelResponse,
} from '@/types/api';

export const analyticsApi = {
  async conversions(periodDays: number, identityId?: string): Promise<ConversionsResponse> {
    const res = await api.get<ConversionsResponse>('/analytics/conversions', {
      params: { periodDays, ...(identityId ? { identityId } : {}) },
    });
    return res.data;
  },

  async insights(periodDays: number): Promise<AnalyticsInsightsResponse> {
    const res = await api.get<AnalyticsInsightsResponse>('/analytics/insights', {
      params: { periodDays },
    });
    return res.data;
  },

  async overview(periodDays = ANALYTICS_DEFAULTS.overviewPeriodDays): Promise<OverviewResponse> {
    const res = await api.get<OverviewResponse>('/analytics/overview', {
      params: { periodDays },
    });
    return res.data;
  },

  async overviewSeries(
    periodDays = ANALYTICS_DEFAULTS.overviewPeriodDays,
    granularity: Granularity = 'DAY',
  ): Promise<OverviewSeriesResponse> {
    const res = await api.get<OverviewSeriesResponse>('/analytics/overview-series', {
      params: { periodDays, granularity },
    });
    return res.data;
  },

  async viewsEvolution(params: EvolutionParams): Promise<TimeSeries> {
    const res = await api.get<TimeSeries>('/analytics/views-evolution', {
      params,
    });
    return res.data;
  },

  async followersEvolution(params: EvolutionParams): Promise<TimeSeries> {
    const res = await api.get<TimeSeries>('/analytics/followers-evolution', {
      params,
    });
    return res.data;
  },

  async topAccounts(params: TopAccountsParams = {}): Promise<TopAccount[]> {
    const res = await api.get<TopAccount[]>('/analytics/top-accounts', {
      params: {
        metric: params.metric ?? 'VIEWS_GROWTH',
        periodDays: params.periodDays ?? ANALYTICS_DEFAULTS.topAccountsPeriodDays,
        limit: params.limit ?? ANALYTICS_DEFAULTS.topAccountsLimit,
      },
    });
    return res.data;
  },

  async identityPerformance(
    periodDays = ANALYTICS_DEFAULTS.overviewPeriodDays,
  ): Promise<IdentityPerformance[]> {
    const res = await api.get<IdentityPerformance[]>('/analytics/identity-performance', {
      params: { periodDays },
    });
    return res.data;
  },

  async accountDashboard(
    id: string,
    periodDays = ANALYTICS_DEFAULTS.overviewPeriodDays,
  ): Promise<AccountDashboardResponse> {
    const res = await api.get<AccountDashboardResponse>(`/analytics/account/${id}/dashboard`, {
      params: { periodDays },
    });
    return res.data;
  },

  async viralReels(
    periodDays = ANALYTICS_DEFAULTS.viralPeriodDays,
    limit = ANALYTICS_DEFAULTS.viralLimit,
  ): Promise<ViralReelResponse[]> {
    const res = await api.get<ViralReelResponse[]>('/analytics/viral-reels', {
      params: { periodDays, limit },
    });
    return res.data;
  },

  async reelsByAccount(id: string, params: PageParams = {}): Promise<Page<ReelResponse>> {
    const res = await api.get<Page<ReelResponse>>(`/analytics/account/${id}/reels`, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? PAGINATION.reelsPageSize,
        sort: params.sort ?? 'publishedAt,desc',
      },
    });
    return res.data;
  },

  async linkReadiness(params: LinkReadinessParams = {}): Promise<LinkReadinessResponse[]> {
    const res = await api.get<LinkReadinessResponse[]>('/analytics/link-readiness', {
      params: {
        periodDays: params.periodDays ?? ANALYTICS_DEFAULTS.linkReadinessPeriodDays,
        limit: params.limit ?? ANALYTICS_DEFAULTS.linkReadinessLimit,
      },
    });
    return res.data;
  },

  async retirementCandidates(
    params: RetirementCandidatesParams = {},
  ): Promise<RetirementCandidateResponse[]> {
    const res = await api.get<RetirementCandidateResponse[]>('/analytics/retirement-candidates', {
      params: {
        minAgeDays: params.minAgeDays ?? ANALYTICS_DEFAULTS.retirementMinAgeDays,
        dailyAvgThreshold:
          params.dailyAvgThreshold ?? ANALYTICS_DEFAULTS.retirementDailyAvgThreshold,
        periodDays: params.periodDays ?? ANALYTICS_DEFAULTS.retirementPeriodDays,
        limit: params.limit ?? ANALYTICS_DEFAULTS.retirementLimit,
      },
    });
    return res.data;
  },

  async efficiency(params: EfficiencyParams = {}): Promise<EfficiencyResponse[]> {
    const res = await api.get<EfficiencyResponse[]>('/analytics/efficiency', {
      params: {
        periodDays: params.periodDays ?? ANALYTICS_DEFAULTS.efficiencyPeriodDays,
        limit: params.limit ?? ANALYTICS_DEFAULTS.efficiencyLimit,
      },
    });
    return res.data;
  },

  async templates(params: TemplatesListParams = {}): Promise<TemplateSummary[]> {
    const res = await api.get<TemplateSummary[]>('/analytics/templates', {
      params: {
        periodDays: params.periodDays ?? ANALYTICS_DEFAULTS.overviewPeriodDays,
        minReels: params.minReels ?? 3,
        sortBy: params.sortBy ?? 'MEDIAN_VIEWS',
        sortDir: params.sortDir ?? 'DESC',
        limit: params.limit ?? ANALYTICS_DEFAULTS.templatesLimit,
        ...(params.identityId ? { identityId: params.identityId } : {}),
      },
    });
    return res.data;
  },

  async templateDetail(
    templateHash: string,
    periodDays = ANALYTICS_DEFAULTS.overviewPeriodDays,
  ): Promise<TemplateDetail> {
    const res = await api.get<TemplateDetail>(`/analytics/templates/${templateHash}`, {
      params: { periodDays },
    });
    return res.data;
  },
};
